import { observer } from "mobx-react-lite";
import type { FC, KeyboardEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useServiceFactory } from "../../../app/appServices";
import { onActivationKey } from "../../../components/a11y/activationKey";
import { Blocked } from "../../../components/blocked/blocked";
import { Loader } from "../../../components/loader/loader";
import { Policy } from "../../../components/policy/policy";
import { Status } from "../../../components/status/status";
import { ErrorState } from "../../../helpers/error";
import { useMount } from "../../../helpers/reactHooks";
import type { Provider } from "../../../types/banks";
import type { PaymentInfo } from "../../../types/payment";
import iconClear from "./resources/icon_clear.svg";
import iconSearch from "./resources/icon_search.svg";
import styles from "./BanksPage.module.scss";

type BanksPageProps = {
  details: PaymentInfo;
};

const focusableIn = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>('[tabindex="0"], a[href]'));

export const BanksPage: FC<BanksPageProps> = observer(({ details }) => {
  const store = useServiceFactory((x) => x.createBanksPageStore());
  const [query, setQuery] = useState<string>("");
  const [bankToShow, selectBank] = useState<Provider | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useMount(async () => {
    await store.load(details.openbankingPaymentRequest.id, details.openbankingPaymentRequest.token);
  });

  useEffect(() => {
    store.doSearch(query);
  }, [query, store]);

  // Move focus into the confirmation dialog while it is open and give it back to the bank that opened it.
  useEffect(() => {
    if (bankToShow) {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // the Confirm button is the first role="button" in the dialog
      dialogRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
      return;
    }
    openerRef.current?.focus();
    openerRef.current = null;
  }, [bankToShow]);

  const handleDialogKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      selectBank(null);
      return;
    }
    if (e.key !== "Tab") {
      return;
    }
    const focusable = focusableIn(e.currentTarget);
    const first = focusable.at(0);
    const last = focusable.at(-1);
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  useEffect(() => {
    if (store.redirectUrl) {
      window.location.href = store.redirectUrl;
    }
  }, [store.redirectUrl]);

  const handleConfirm = async (paymentId: string, token: string, bankId: string): Promise<void> => {
    await store.selectProvider(paymentId, token, bankId);
  };

  const render = (): ReactNode => {
    if (store.state === "loading") {
      return <Loader className={styles.loader} />;
    }
    if (store.state instanceof ErrorState) {
      return (
        <>
          <Status type="error" backUrl={details.backUrl}>
            <div>Hmm, we can&apos;t find</div>
            <div>who this link belongs to.</div>
            <div>Please, check if you copied</div>
            <div>the address correctly.</div>
          </Status>
          <Blocked
            name={details.toAccountName}
            accountNumber={details.openbankingPaymentRequest.toAccountNumber}
            sortCode={details.openbankingPaymentRequest.toAccountSortCode}
          />
        </>
      );
    }

    const { config } = store.state;

    return (
      <div className={`${styles.panelBanks} ${bankToShow && styles.overlay}`}>
        {bankToShow && (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- modal dialog handles Escape and keeps Tab focus inside
          <div
            ref={dialogRef}
            className={styles.confirmationOverlay}
            role="dialog"
            aria-modal="true"
            aria-label={`Confirm payment with ${bankToShow.displayable_name}`}
            onKeyDown={handleDialogKeyDown}
          >
            {store.redirectUrl && (
              <div className={styles.confirmationLink}>
                If you are not redirected,
                <a href={store.redirectUrl}>tap here</a>
              </div>
            )}
            <div className={styles.confirmationBlock}>
              <div className={styles.confirmationText}>You will now be redirected to {bankToShow.displayable_name}</div>
              <div
                className={styles.confirmationButtonPrimary}
                role="button"
                tabIndex={0}
                onClick={() => {
                  void handleConfirm(config.paymentId, config.token, bankToShow.id);
                }}
                onKeyDown={onActivationKey(() => {
                  void handleConfirm(config.paymentId, config.token, bankToShow.id);
                })}
              >
                Confirm
              </div>
              <div
                className={styles.confirmationButton}
                role="button"
                tabIndex={0}
                onClick={() => {
                  selectBank(null);
                }}
                onKeyDown={onActivationKey(() => {
                  selectBank(null);
                })}
              >
                Cancel
              </div>
            </div>
          </div>
        )}
        <div className={styles.panelBanksTitle} role="heading" aria-level={2}>
          Choose your bank to make this payment
        </div>
        <div className={styles.panelBanksSearch}>
          <input
            placeholder="Search"
            aria-label="Search banks"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          {!query && <img alt="" src={iconSearch} className={styles.panelBanksSearchIcon} />}
          {query && (
            <img
              alt="Clear search"
              src={iconClear}
              className={styles.panelBanksClearIcon}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role -- clear icon stays an img to keep its styling
              role="button"
              tabIndex={0}
              onClick={() => {
                setQuery("");
              }}
              onKeyDown={onActivationKey(() => {
                setQuery("");
              })}
            />
          )}
        </div>
        <div className={styles.banksListContainer}>
          {store.state.list.length === 0 && (
            <div className={styles.banksListEmpty} data-test-id="banks-empty">
              There are no results for &quot;{query}&quot;
              <br />
              Try searching for another or choose from the list of available payment methods.
            </div>
          )}
          {!!store.state.list.length && (
            <div className={styles.banksListContainerContent} data-test-id="banks-list">
              {store.state.list.map((bank) => (
                <div
                  key={bank.id}
                  className={styles.panelBanksItemBlock}
                  role="button"
                  // the sticker is part of what sighted users see, so it is part of the name too
                  aria-label={bank.label ? `${bank.displayable_name}, ${bank.label}` : bank.displayable_name}
                  tabIndex={0}
                  onClick={() => {
                    selectBank(bank);
                  }}
                  onKeyDown={onActivationKey(() => {
                    selectBank(bank);
                  })}
                >
                  {bank.label === "business" && (
                    <div className={styles.stickerBox}>
                      <div className={styles.sticker}>BUSINESS</div>
                    </div>
                  )}
                  {bank.label === "corporate" && (
                    <div className={styles.stickerBox}>
                      <div className={styles.corporateSticker}>CORPORATE</div>
                    </div>
                  )}
                  <img src={bank.logo} alt={bank.displayable_name} />
                </div>
              ))}
            </div>
          )}
        </div>
        {!!store.state.list.length && (
          <div className={styles.banksListFooterContainer}>
            <Policy raw paymentType="banks" className={styles.banksListFooter} />
          </div>
        )}
      </div>
    );
  };

  return <>{render()}</>;
});
